/*
 * Sabre Digital: the slide-out basket.
 * Loaded by snippets/popover.liquid on every page that has the basket.
 * Markup: snippets/mini-cart.liquid, sections/cart-upsell.liquid, snippets/cart-upsell-item.liquid.
 * Styles: assets/sabre-cart.css.
 *
 * What it does:
 *  1. Quantity and remove. One request changes the basket and brings back the basket's new HTML.
 *  2. "Complete your order". Loads the add-ons for the dearest item in the basket (then the next ones
 *     if it has none): accessories only, never another bike. One tap adds an add-on; one with colours or sizes opens a small chooser inside its
 *     row first.
 *  3. Delivery. Asks Shopify what delivery costs for exactly what is in the basket, the same way the
 *     delivery block on the product page does, and shows the lowest price.
 *
 * The theme (and the product page add-ons) still refresh the basket themselves after Add To Basket.
 * They do it by swapping the HTML inside [data-mini-cart], so everything here listens on that element
 * and sets itself up again whenever its contents change.
 */
(function () {
  'use strict';

  var box = document.querySelector('[data-mini-cart]');
  if (!box || !window.fetch || !box.closest) return;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };
  var popover = box.closest('[data-popover]');
  var tab = box.closest('[data-popover-tab]');
  var busy = false;
  var justAdded = '';

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  function request(path, body) {
    var options = { credentials: 'same-origin', headers: body ? JSON_HEADERS : { Accept: 'application/json' } };
    if (body) {
      options.method = 'POST';
      options.body = JSON.stringify(body);
    }
    return fetch(ROOT + path, options).then(function (response) {
      return response.json().then(function (json) {
        if (!response.ok) throw new Error((json && (json.description || json.message)) || 'Request failed');
        return json;
      });
    });
  }

  function basketHtml(json) {
    return json && json.sections && json.sections['mini-cart'];
  }

  function setBusy(state) {
    busy = state;
    if (state) box.classList.add('is-busy');
    else box.classList.remove('is-busy');
  }

  /* Puts new basket HTML in place. Everything else follows from the watcher at the bottom. */
  function render(html) {
    box.innerHTML = html;
  }

  /* Used when Shopify did not send the basket HTML back with a change. */
  function refresh() {
    return request('?sections=mini-cart').then(function (data) {
      if (data && data['mini-cart']) render(data['mini-cart']);
    });
  }

  /* ---------- The panel ---------- */

  function isOpen() {
    return !!(popover && tab && popover.classList.contains('active') && tab.classList.contains('active'));
  }

  /* Marks the panel while the basket (not search or account) is the tab on show. */
  function markPanel() {
    if (!popover || !tab) return;

    /* Lets the stylesheet move things out of the way while the panel is open (see sabre-cart.css). */
    var root = document.documentElement;
    if (popover.classList.contains('active')) root.classList.add('sabre-panel-open');
    else root.classList.remove('sabre-panel-open');

    var showing = tab.classList.contains('active');
    if (showing !== popover.classList.contains('sabre-cart-open')) {
      if (showing) popover.classList.add('sabre-cart-open');
      else popover.classList.remove('sabre-cart-open');
    }
    if (isOpen()) loadDelivery();
  }

  /* ---------- Header count ---------- */

  function setCount() {
    var cart = box.querySelector('[data-sabre-cart]');
    if (!cart) return;
    var count = parseInt(cart.getAttribute('data-count'), 10) || 0;
    each(document.querySelectorAll('[data-cart-count]'), function (badge) {
      var label = count > 99 ? '99+' : String(count);
      if (badge.textContent.replace(/\s+/g, '') !== label) badge.textContent = label;
      if (count > 0) badge.classList.add('active');
      else badge.classList.remove('active');
    });
  }

  /* ---------- 1. Quantity and remove ---------- */

  function lineMessage(line, text) {
    var message = line && line.querySelector('[data-sabre-line-msg]');
    if (!message) return;
    message.textContent = text;
    message.hidden = false;
  }

  function change(line, quantity) {
    if (busy || !line || isNaN(quantity)) return;
    var key = line.getAttribute('data-sabre-line');
    var number = line.querySelector('.sabre-qty__n');
    var before = number ? number.textContent : '';

    setBusy(true);
    line.classList.add('is-busy');
    if (number && quantity > 0) number.textContent = String(quantity);

    request('cart/change.js', { id: key, quantity: quantity, sections: 'mini-cart', sections_url: ROOT })
      .then(function (json) {
        var html = basketHtml(json);
        if (html) render(html);
        else return refresh();
      })
      .catch(function (error) {
        if (number) number.textContent = before;
        line.classList.remove('is-busy');
        var text = error && error.message && error.message !== 'Request failed' ? error.message : '';
        lineMessage(line, text || 'Sorry, we could not update your basket. Please try again.');
      })
      .then(function () {
        setBusy(false);
      });
  }

  /* ---------- 2. Complete your order ---------- */

  var upsellCache = {};
  var upsellShown = '';
  var upsellRun = 0;

  function hasRows(html) {
    return !!html && html.indexOf('sabre-upsell__list') > -1;
  }

  function fetchUpsell(productId, intent) {
    return fetch(
      ROOT +
        'recommendations/products?product_id=' +
        encodeURIComponent(productId) +
        '&section_id=cart-upsell&intent=' +
        intent +
        '&limit=10',
      { credentials: 'same-origin' }
    ).then(function (response) {
      return response.ok ? response.text() : '';
    });
  }

  /* Takes rows out that are now in the basket, so the block never shows something just added. */
  function prune(wrap, inCart) {
    var ids = inCart.split(',').filter(Boolean);
    each(wrap.querySelectorAll('[data-sabre-up]'), function (row) {
      var mine = (row.getAttribute('data-products') || '').split(',').filter(Boolean);
      var clash = mine.some(function (id) {
        return ids.indexOf(id) > -1;
      });
      if (clash) row.parentNode.removeChild(row);
    });
    if (!wrap.querySelector('[data-sabre-up]')) wrap.innerHTML = '';
  }

  /* Accessories for one product: the add-ons set for it first, then Shopify's related products. */
  function upsellFor(productId) {
    return fetchUpsell(productId, 'complementary').then(function (html) {
      return hasRows(html) ? html : fetchUpsell(productId, 'related');
    });
  }

  /* Tries the dearest item in the basket first, then the next ones, until one has accessories to offer. */
  function upsellForAny(productIds, index) {
    if (index >= productIds.length) return Promise.resolve('');
    return upsellFor(productIds[index]).then(function (html) {
      return hasRows(html) ? html : upsellForAny(productIds, index + 1);
    });
  }

  function loadUpsell() {
    var wrap = box.querySelector('[data-sabre-upsell]');
    if (!wrap) return;
    var inCart = wrap.getAttribute('data-in-cart') || '';
    var productIds = (wrap.getAttribute('data-sabre-upsell') || '').split(',').filter(function (id, index, list) {
      return id && list.indexOf(id) === index;
    });
    if (!productIds.length) return;

    var key = productIds.join(',') + '|' + inCart;
    if (upsellCache[key] !== undefined) {
      wrap.innerHTML = upsellCache[key];
      upsellShown = upsellCache[key];
      return;
    }

    /* While the new list is on its way, keep showing the last one so the basket does not jump. */
    if (upsellShown) {
      wrap.innerHTML = upsellShown;
      prune(wrap, inCart);
    }

    var run = ++upsellRun;
    upsellForAny(productIds, 0)
      .then(function (html) {
        if (!hasRows(html)) html = '';
        upsellCache[key] = html;
        if (run !== upsellRun) return;
        var current = box.querySelector('[data-sabre-upsell]');
        if (!current) return;
        current.innerHTML = html;
        upsellShown = html;
      })
      .catch(function () {});
  }

  function rowMessage(row, text) {
    var message = row.querySelector('[data-up-msg]');
    if (!message) return;
    message.textContent = text;
    message.hidden = false;
  }

  function addUpsell(row, variantId) {
    if (busy || !variantId) return;
    setBusy(true);
    row.classList.add('is-loading');

    request('cart/add.js', {
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: 'mini-cart',
      sections_url: ROOT
    })
      .then(function (json) {
        justAdded = String(variantId);
        var html = basketHtml(json);
        if (html) render(html);
        else return refresh();
      })
      .catch(function (error) {
        row.classList.remove('is-loading');
        var text = error && error.message && error.message !== 'Request failed' ? error.message : '';
        rowMessage(row, text || 'Sorry, we could not add that. Please try again.');
      })
      .then(function () {
        setBusy(false);
      });
  }

  function setPanel(row, open) {
    var panel = row.querySelector('[data-up-panel]');
    var button = row.querySelector('[data-up-choose]');
    if (!panel || !button) return;
    var label = button.querySelector('[data-up-btn-label]');

    panel.hidden = !open;
    button.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) row.classList.add('is-open');
    else row.classList.remove('is-open');
    if (label) label.textContent = open ? 'Close' : label.getAttribute('data-label') || 'Choose';
    if (!open) return;

    /* Only one chooser open at a time. */
    each(box.querySelectorAll('[data-sabre-up].is-open'), function (other) {
      if (other !== row) setPanel(other, false);
    });

    /* With sizes to pick, start on a colour so its sizes are already showing. */
    if (panel.querySelector('[data-up-sizes]')) {
      var chosen =
        panel.querySelector('[data-up-colour][aria-pressed="true"]') ||
        panel.querySelector('[data-up-colour][data-default="true"]') ||
        panel.querySelector('[data-up-colour]');
      if (chosen) selectColour(row, chosen, true);
    }
  }

  /* Shows a colour's photo, price and sizes. A colour with nothing more to choose is added straight away. */
  function selectColour(row, chip, quiet) {
    var panel = row.querySelector('[data-up-panel]');
    if (!panel) return;
    var single = chip.getAttribute('data-single');
    var id = chip.getAttribute('data-up-colour');

    each(panel.querySelectorAll('[data-up-colour]'), function (other) {
      other.setAttribute('aria-pressed', other === chip && !(quiet && single) ? 'true' : 'false');
    });

    var image = row.querySelector('[data-up-img]');
    var price = row.querySelector('[data-up-price]');
    if (image && chip.getAttribute('data-img')) image.src = chip.getAttribute('data-img');
    if (price && chip.getAttribute('data-price')) price.textContent = chip.getAttribute('data-price');

    var sizes = panel.querySelector('[data-up-sizes]');
    if (sizes) {
      var found = false;
      each(sizes.querySelectorAll('[data-up-variants]'), function (group) {
        var match = group.getAttribute('data-up-variants') === id;
        group.hidden = !match;
        if (match) found = true;
      });
      sizes.hidden = !found;
    }

    if (!quiet && single) addUpsell(row, single);
  }

  /* ---------- 3. Delivery ---------- */

  var API_URL = '/api/2026-07/graphql.json';
  var ZIP_KEY = 'sabrePdpPostcode'; /* the postcode a shopper checked on a product page */
  var RATE_PREFIX = 'sabreCartRates:';
  var RATE_MINUTES = 30;
  var OPTIONS_FRAGMENT =
    'deliveryGroups(first: 5) { edges { node { deliveryOptions { title estimatedCost { amount currencyCode } } } } }';
  var CREATE_QUERY =
    'mutation ($input: CartInput!) { cartCreate(input: $input) { cart { id ' + OPTIONS_FRAGMENT + ' } userErrors { message } } }';
  var CART_QUERY = 'query ($id: ID!) { cart(id: $id) { id ' + OPTIONS_FRAGMENT + ' } }';
  var rateMemo = {};
  var ratePending = {};

  function session(action, key, value) {
    try {
      if (action === 'get') return window.sessionStorage.getItem(key);
      window.sessionStorage.setItem(key, value);
    } catch (e) {}
    return null;
  }

  function tidyPostcode(raw) {
    var value = String(raw || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!/^[A-Z]{1,2}[0-9][A-Z0-9]?([0-9][A-Z]{2})?$/.test(value)) return '';
    return value.length > 4 ? value.slice(0, -3) + ' ' + value.slice(-3) : value;
  }

  function money(amount, currency) {
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency || 'GBP' }).format(amount);
    } catch (e) {
      return '£' + Number(amount).toFixed(2);
    }
  }

  function post(query, variables) {
    return fetch(API_URL, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ query: query, variables: variables })
    }).then(function (response) {
      if (!response.ok) throw new Error('HTTP ' + response.status);
      return response.json();
    });
  }

  function optionsFrom(cart) {
    var list = [];
    var edges = (cart && cart.deliveryGroups && cart.deliveryGroups.edges) || [];
    edges.forEach(function (edge) {
      ((edge.node && edge.node.deliveryOptions) || []).forEach(function (option) {
        if (!option || !option.estimatedCost) return;
        list.push({
          title: String(option.title || '').trim(),
          amount: parseFloat(option.estimatedCost.amount),
          currency: option.estimatedCost.currencyCode
        });
      });
    });
    return list;
  }

  /* A throwaway basket with the same items and a delivery postcode: Shopify answers with the real rates. */
  function createCart(lines, zip, legacyShape) {
    var input = { lines: lines, buyerIdentity: { countryCode: 'GB' } };
    if (legacyShape) {
      input.buyerIdentity.deliveryAddressPreferences = [{ deliveryAddress: { country: 'GB', zip: zip } }];
    } else {
      input.delivery = { addresses: [{ selected: true, address: { deliveryAddress: { countryCode: 'GB', zip: zip } } }] };
    }
    return post(CREATE_QUERY, { input: input }).then(function (json) {
      if (json.errors && !legacyShape) return createCart(lines, zip, true);
      if (json.errors) throw new Error('Storefront API error');
      var cart = json.data && json.data.cartCreate && json.data.cartCreate.cart;
      if (!cart) throw new Error('No cart returned');
      return cart;
    });
  }

  /* Rates can take a moment to calculate, so an empty answer is checked once more. */
  function fetchRates(lines, zip) {
    return createCart(lines, zip, false).then(function (cart) {
      var options = optionsFrom(cart);
      if (options.length) return options;
      return new Promise(function (resolve) {
        setTimeout(resolve, 900);
      })
        .then(function () {
          return post(CART_QUERY, { id: cart.id });
        })
        .then(function (json) {
          return optionsFrom(json.data && json.data.cart);
        });
    });
  }

  function rates(key, lines, zip) {
    if (rateMemo[key]) return Promise.resolve(rateMemo[key]);
    var stored = session('get', RATE_PREFIX + key);
    if (stored) {
      try {
        var parsed = JSON.parse(stored);
        if (parsed && Date.now() - parsed.at < RATE_MINUTES * 60000) {
          rateMemo[key] = parsed.options;
          return Promise.resolve(parsed.options);
        }
      } catch (e) {}
    }
    if (!ratePending[key]) {
      ratePending[key] = fetchRates(lines, zip).then(
        function (options) {
          delete ratePending[key];
          if (options.length) {
            rateMemo[key] = options;
            session('set', RATE_PREFIX + key, JSON.stringify({ at: Date.now(), options: options }));
          }
          return options;
        },
        function (error) {
          delete ratePending[key];
          throw error;
        }
      );
    }
    return ratePending[key];
  }

  /* The lowest price, and the Express price beside it when there is one: "Free, or Express \u00a38.99". */
  function deliveryWords(options) {
    var sorted = options.slice().sort(function (a, b) {
      return a.amount - b.amount;
    });
    var cheapest = sorted[0];
    var express = null;
    sorted.forEach(function (option) {
      if (!express && option !== cheapest && /express/i.test(option.title || '')) express = option;
    });

    var words = cheapest.amount === 0 ? 'Free' : money(cheapest.amount, cheapest.currency);
    if (express && express.amount > cheapest.amount) return words + ', or Express ' + money(express.amount, express.currency);
    if (sorted.length > 1 && cheapest.amount > 0) return 'From ' + words;
    return words;
  }

  var deliveryShown = null;

  function showDelivery(row, options, place) {
    if (!options || !options.length) return;
    var label = row.querySelector('[data-sabre-delivery-label]');
    var price = row.querySelector('[data-sabre-delivery-price]');
    if (!label || !price) return;
    deliveryShown = { label: 'Delivery to ' + place, price: deliveryWords(options) };
    label.textContent = deliveryShown.label;
    price.textContent = deliveryShown.price;
    row.setAttribute('data-state', 'ready');
  }

  function deliveryDetails(row) {
    var lines = (row.getAttribute('data-lines') || '')
      .split(',')
      .filter(Boolean)
      .map(function (pair) {
        var parts = pair.split(':');
        return { merchandiseId: 'gid://shopify/ProductVariant/' + parts[0], quantity: parseInt(parts[1], 10) || 1 };
      });
    var saved = tidyPostcode(session('get', ZIP_KEY));
    var fallback = row.getAttribute('data-default-zip') || '';
    var zip = saved || tidyPostcode(fallback) || fallback;
    return {
      lines: lines,
      zip: zip,
      place: saved ? saved : row.getAttribute('data-area') || 'the UK',
      key: (row.getAttribute('data-lines') || '') + '|' + zip
    };
  }

  /* No price to show: back to the plain wording. */
  function plainDelivery(row, state) {
    var label = row.querySelector('[data-sabre-delivery-label]');
    var price = row.querySelector('[data-sabre-delivery-price]');
    if (label) label.textContent = 'Delivery';
    if (price) price.textContent = 'Worked out at checkout';
    deliveryShown = null;
    row.setAttribute('data-state', state);
  }

  /* A price already known for this exact basket is shown at once, with no request. */
  function showKnownDelivery() {
    var row = box.querySelector('[data-sabre-delivery]');
    if (!row) return;
    var details = deliveryDetails(row);
    if (rateMemo[details.key]) {
      showDelivery(row, rateMemo[details.key], details.place);
    } else if (deliveryShown && isOpen()) {
      /* The basket just changed: the last price stays, dimmed, until the new one arrives. */
      var label = row.querySelector('[data-sabre-delivery-label]');
      var price = row.querySelector('[data-sabre-delivery-price]');
      if (label && price) {
        label.textContent = deliveryShown.label;
        price.textContent = deliveryShown.price;
        row.setAttribute('data-state', 'stale');
      }
    }
  }

  /* Only asked for while the basket is open, so browsing pages costs nothing. */
  function loadDelivery() {
    var row = box.querySelector('[data-sabre-delivery]');
    var state = row && row.getAttribute('data-state');
    if (!row || (state && state !== 'stale')) return;
    var details = deliveryDetails(row);
    if (!details.lines.length || !details.zip) return;

    if (!state) row.setAttribute('data-state', 'loading');
    else row.setAttribute('data-state', 'stale-loading');
    rates(details.key, details.lines, details.zip)
      .then(function (options) {
        var current = box.querySelector('[data-sabre-delivery]');
        if (!current || deliveryDetails(current).key !== details.key) return;
        if (options.length) showDelivery(current, options, details.place);
        else plainDelivery(current, 'none');
      })
      .catch(function () {
        var current = box.querySelector('[data-sabre-delivery]');
        if (current) plainDelivery(current, 'error');
      });
  }

  /* ---------- Clicks ---------- */

  box.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var quantity = target.closest('[data-sabre-qty]');
    if (quantity) {
      event.preventDefault();
      if (!quantity.disabled) change(quantity.closest('[data-sabre-line]'), parseInt(quantity.getAttribute('data-sabre-qty'), 10));
      return;
    }

    var removeLink = target.closest('[data-sabre-remove]');
    if (removeLink) {
      event.preventDefault();
      change(removeLink.closest('[data-sabre-line]'), 0);
      return;
    }

    var row = target.closest('[data-sabre-up]');
    if (!row) return;

    var addButton = target.closest('[data-up-add]');
    if (addButton) {
      event.preventDefault();
      addUpsell(row, addButton.getAttribute('data-up-add'));
      return;
    }

    var chooseButton = target.closest('[data-up-choose]');
    if (chooseButton) {
      event.preventDefault();
      setPanel(row, chooseButton.getAttribute('aria-expanded') !== 'true');
      return;
    }

    var colour = target.closest('[data-up-colour]');
    if (colour) {
      event.preventDefault();
      selectColour(row, colour, false);
      return;
    }

    var variant = target.closest('[data-up-variant]');
    if (variant && !variant.disabled) {
      event.preventDefault();
      addUpsell(row, variant.getAttribute('data-up-variant'));
    }
  });

  /* ---------- Set-up, repeated whenever the basket's HTML is replaced ---------- */

  function setUp() {
    setCount();
    loadUpsell();
    showKnownDelivery();
    if (isOpen()) loadDelivery();

    if (justAdded) {
      var line = box.querySelector('[data-sabre-line][data-variant="' + justAdded + '"]');
      justAdded = '';
      if (line) {
        line.classList.add('is-new');
        var scroller = box.closest('[data-popover-content]');
        if (scroller) scroller.scrollTop = 0;
      }
    }
  }

  if ('MutationObserver' in window) {
    new MutationObserver(setUp).observe(box, { childList: true });
    if (tab) new MutationObserver(markPanel).observe(tab, { attributes: true, attributeFilter: ['class'] });
    if (popover) new MutationObserver(markPanel).observe(popover, { attributes: true, attributeFilter: ['class'] });
  }

  markPanel();
  setUp();
})();
